import KEY_EXPORT_STUDENT_REPORT_QUERY from 'graph/reports/key-export-student-report-query';
import getClassroomPrimaryEducator from 'utilities/get-classroom-primary-eduator';
import { ReportWorkbook, ReportWorksheet } from 'components/ReportsModule/types';
import {
  KeyClassroomEngagementReportResult,
  KeyClassroomEngagementStudentResult
} from 'types/schema-generated-types';

export const STUDENT_WORKSHEETS: ReportWorksheet<any>[] = [
  {
    name: 'Student Overview',
    parseQueryData: ({ students }): any[][] => students.students.map(mapStudentOverviewWorksheet)
  },
  {
    name: 'Student Spot Checks',
    parseQueryData: ({ students }): any[][] =>
      students.students.flatMap(mapStudentSpotChecksWorksheet)
  },
  {
    name: 'Student Assessments',
    parseQueryData: ({ students }): any[][] =>
      students.students.flatMap(mapStudentAssessmentsWorksheet)
  }
];

export const STUDENT_WORKBOOK: ReportWorkbook<{
  students: KeyClassroomEngagementReportResult;
}> = {
  query: KEY_EXPORT_STUDENT_REPORT_QUERY,
  getEntityName: ({ students: { students } }): string => {
    return `${students?.[0]?.student?.firstName} ${students?.[0].student?.lastName}`;
  },
  worksheets: [...STUDENT_WORKSHEETS]
};

const mapBasicStudentData = (data: KeyClassroomEngagementStudentResult): any => {
  const activeClassrooms = data.student?.classrooms?.filter(classroom => !classroom.dateArchived);
  const educators = activeClassrooms?.map(classroom => getClassroomPrimaryEducator(classroom));
  const organization = [
    ...(data.student?.organization?.rootOrganization?.id !== data.student?.organization?.id
      ? [data.student?.organization?.rootOrganization?.name]
      : []),
    data.student?.organization?.name
  ].join(' - ');

  return {
    studentFirstName: data.student?.firstName,
    studentLastName: data.student?.lastName,
    studentSisId: data.student?.sisId,
    grade: data.student?.gradeLevel,
    license: data.productCurriculumCode,
    classroomName: activeClassrooms?.map(classroom => classroom?.name).join(' ,'),
    classroomSisId: activeClassrooms?.map(classroom => classroom?.sisId).join(' ,'),
    educator: educators?.map(educator => `${educator?.firstName} ${educator?.lastName}`).join(' ,'),
    educatorSisId: educators?.map(educator => educator?.sisId).join(' ,'),
    organization
  };
};

const mapStudentOverviewWorksheet = (data: KeyClassroomEngagementStudentResult): any => {
  return {
    ...mapBasicStudentData(data),
    lastLogin: data.lastLoginDate,
    curriculumWeek: data.curriculumWeek,
    overallCurriculumProgress: data.curriculumProgressPercentage,
    activitiesCompletedTotal: data.totalActivitiesCompletedCount,
    averageWeeklyTimeSpentTotal: data.averageWeeklyMinutesSpent,
    spotChecksCompleted: data.spotChecks?.totalCompletedCount ?? 0,
    spotChecksAverageSpeed: data.spotChecks?.averageWpm ?? 0,
    spotChecksAverageAccuracy: data.spotChecks?.averageAccuracy ?? 0,
    assessmentsCompleted: data.scheduledActivities?.assessmentCompletedCount ?? 0,
    assessmentsAverageSpeed: data.scheduledActivities?.assessmentAverageWpm ?? 0,
    assessmentsAverageAccuracy: data.scheduledActivities?.assessmentAverageAccuracy ?? 0
  };
};

const mapStudentSpotChecksWorksheet = (data: KeyClassroomEngagementStudentResult): any[] => {
  return (
    data.spotChecks?.activities?.map(item => ({
      ...mapBasicStudentData(data),
      sportCheckType: item.template,
      dateCompleted: new Date(item.dateCompleted),
      spotCheckWeek: item.week,
      speedWpm: item.wpm,
      accuracy: item.accuracy
    })) ?? []
  );
};

const mapStudentAssessmentsWorksheet = (data: KeyClassroomEngagementStudentResult): any[] => {
  return (
    data.scheduledActivities?.activities?.map(item => ({
      ...mapBasicStudentData(data),
      dateCompleted: new Date(item.dateCompleted),
      speedWpm: item.wpm,
      accuracy: item.accuracy
    })) ?? []
  );
};
